import { createContext, useContext, useMemo } from 'react';
import { useTagEvents } from './TagEventsContext';
import { useBackendHealth } from '../hooks/useBackendHealth';
import { computeAggregateStatus } from './computeAggregateStatus';

/**
 * @typedef {'connected' | 'disconnected' | 'degraded'} ConnectionStatus
 * @typedef {'ok' | 'degraded' | 'down' | 'unknown'} BackendStatus
 * @typedef {{
 *   status: ConnectionStatus,
 *   tagSocketConnected: boolean,
 *   backendStatus: BackendStatus,
 *   components: Record<string, 'up'|'down'|'disabled'>,
 *   fetchedAt: number | null,
 * }} ConnectionHealth
 */

const ConnectionHealthContext = createContext({
  status: 'disconnected',
  tagSocketConnected: false,
  backendStatus: 'unknown',
  components: {},
  fetchedAt: null,
});

/**
 * Provides connection health derived from TagEventsContext + backend /api/ready.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {{
 *   tagSocketConnected?: boolean,
 *   backendStatus?: BackendStatus,
 *   components?: object,
 *   fetchedAt?: number | null,
 *   status?: ConnectionStatus,
 * }} [props.value] Test-only seam: when provided, bypasses live sources and
 *   derives status from these inputs. Production callers should omit this prop.
 */
export function ConnectionHealthProvider({ children, value }) {
  if (value !== undefined) {
    const tagSocketConnected = Boolean(value.tagSocketConnected);
    const backendStatus = value.backendStatus ?? 'ok';
    const derived = {
      tagSocketConnected,
      backendStatus,
      components: value.components ?? {},
      fetchedAt: value.fetchedAt ?? null,
      status:
        value.status ??
        computeAggregateStatus({ tagSocketConnected, backendStatus }),
    };
    return (
      <ConnectionHealthContext.Provider value={derived}>
        {children}
      </ConnectionHealthContext.Provider>
    );
  }
  return <ProviderFromSources>{children}</ProviderFromSources>;
}

function ProviderFromSources({ children }) {
  const tagEvents = useTagEvents();
  const tagSocketConnected = Boolean(tagEvents?.isConnected);
  const backendHealth = useBackendHealth({ refreshTrigger: tagSocketConnected });

  const derived = useMemo(
    () => ({
      status: computeAggregateStatus({
        tagSocketConnected,
        backendStatus: backendHealth.status,
      }),
      tagSocketConnected,
      backendStatus: backendHealth.status,
      components: backendHealth.components,
      fetchedAt: backendHealth.fetchedAt,
    }),
    [tagSocketConnected, backendHealth],
  );

  return (
    <ConnectionHealthContext.Provider value={derived}>
      {children}
    </ConnectionHealthContext.Provider>
  );
}

export function useConnectionHealth() {
  return useContext(ConnectionHealthContext);
}
